import React, { useMemo, useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalContent, ModalActions } from '@msi/cobalt-react/modal';
import Button from '@msi/cobalt-react/button';
import { Api } from '../../types/apiData';

interface CompareVersionsModalProps {
    isOpen: boolean;
    versions: Api[];
    defaultFromId?: string;
    defaultToId?: string;
    onCompare: (fromVersionNumber: string, toVersionNumber: string) => void;
    onCancel: () => void;
}

/** Numerically compares dotted version strings (e.g. "7.1.0" > "6.0.0" > "2.0.0"), falling back to string compare. */
const compareVersionNumbers = (a?: string, b?: string): number => {
    const partsA = (a || '').split('.').map((n) => parseInt(n, 10));
    const partsB = (b || '').split('.').map((n) => parseInt(n, 10));
    const len = Math.max(partsA.length, partsB.length);
    for (let i = 0; i < len; i++) {
        const numA = partsA[i] ?? 0;
        const numB = partsB[i] ?? 0;
        if (Number.isNaN(numA) || Number.isNaN(numB)) {
            return (a || '').localeCompare(b || '');
        }
        if (numA !== numB) {
            return numA - numB;
        }
    }
    return 0;
};

/** Lets the user pick the two versions to compare before navigating to the dedicated compare page. */
const CompareVersionsModal: React.FC<CompareVersionsModalProps> = ({
    isOpen,
    versions,
    defaultFromId,
    defaultToId,
    onCompare,
    onCancel,
}) => {
    const [fromId, setFromId] = useState(defaultFromId || '');
    const [toId, setToId] = useState(defaultToId || '');

    const sortedVersions = useMemo(
        () =>
            [...versions].sort((a, b) =>
                compareVersionNumbers(b.latestApiVersionNumber, a.latestApiVersionNumber)
            ),
        [versions]
    );

    useEffect(() => {
        if (isOpen) {
            setFromId(defaultFromId || sortedVersions[1]?.id || sortedVersions[0]?.id || '');
            setToId(defaultToId || sortedVersions[0]?.id || '');
        }
    }, [isOpen, defaultFromId, defaultToId, sortedVersions]);

    const modalStyle = useMemo(() => ({
        overlay: { zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' },
        content: {
            zIndex: 4001,
            inset: 'auto',
            position: 'relative',
            border: 'none',
            padding: 0,
            overflow: 'visible',
            width: 'min(480px, calc(100vw - 48px))',
            maxWidth: '480px',
            margin: '0 auto',
            background: 'transparent',
        },
    }), []);

    const canCompare = !!fromId && !!toId && fromId !== toId;

    const handleCompare = () => {
        const fromVersion = sortedVersions.find((v) => v.id === fromId)?.latestApiVersionNumber;
        const toVersion = sortedVersions.find((v) => v.id === toId)?.latestApiVersionNumber;
        if (fromVersion && toVersion) {
            onCompare(fromVersion, toVersion);
        }
    };

    return (
        <Modal handleCloseModal={onCancel} showModal={isOpen} style={modalStyle}>
            <div className="compare-versions-modal">
                <ModalHeader>Compare Versions</ModalHeader>
                <ModalContent>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <label>
                            From version
                            <select
                                className="form-select mt-1"
                                value={fromId}
                                onChange={(e) => setFromId(e.target.value)}
                            >
                                {sortedVersions.map((v) => (
                                    <option key={v.id} value={v.id}>
                                        Version {v.latestApiVersionNumber}{v.isLatestVersion ? ' (Latest)' : ''}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label>
                            To version
                            <select
                                className="form-select mt-1"
                                value={toId}
                                onChange={(e) => setToId(e.target.value)}
                            >
                                {sortedVersions.map((v) => (
                                    <option key={v.id} value={v.id}>
                                        Version {v.latestApiVersionNumber}{v.isLatestVersion ? ' (Latest)' : ''}
                                    </option>
                                ))}
                            </select>
                        </label>
                        {fromId && toId && fromId === toId && (
                            <p className="text-danger mb-0">Please choose two different versions.</p>
                        )}
                    </div>
                </ModalContent>
                <ModalActions>
                    <Button text="Cancel" onClick={onCancel} />
                    <Button text="Compare" variant="primary" disabled={!canCompare} onClick={handleCompare} />
                </ModalActions>
            </div>
        </Modal>
    );
};

export default CompareVersionsModal;
